// src/pages/ApplicationWonTitle/components/WonTitleTable/WonTitleTable.tsx
import { Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text, Image } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { getConfigFromStorage } from '@/utils/config';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { WonTitle } from '@/model';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { useTranslation } from 'react-i18next';

export interface WonTitleTableProps {
  data: WonTitle[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick: (item: WonTitle) => void;
  onDeleteClick: (item: WonTitle) => void;
}

export const WonTitleTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onEditClick,
  onDeleteClick,
}: WonTitleTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<{
    team_name: string;
    team_image: string;
    created_at: string;
    competition_name: string;
    year: number;
  }>();
  const isMultiTeam = getConfigFromStorage()?.is_multi_team ?? true;

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.application_won_title.components.won_title_table.won_title_table_created_at_header'),
    }),

    columnHelper.accessor('team_name', {
      cell: props => {
        const { team_image, team_name } = props.row.original;
        return (
          <Flex alignItems='center' gap={2}>
            {team_image ? <Image src={team_image} alt={team_name || ''} w='24px' h='24px' objectFit='contain' /> : null}
            <Text fontWeight='bold'>{team_name || ''}</Text>
          </Flex>
        );
      },
      header: t('pages.application_won_title.components.won_title_table.won_title_table_team_header'),
    }),

    columnHelper.accessor('competition_name', {
      cell: props => props.getValue(),
      header: t('pages.application_won_title.components.won_title_table.won_title_table_competition_header'),
    }),
    columnHelper.accessor('year', {
      cell: props => props.getValue(),
      header: t('pages.application_won_title.components.won_title_table.won_title_table_year_header'),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('pages.application_won_title.components.won_title_table.won_title_table_actions_header'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_won_title.components.won_title_table.won_title_table_actions_menu_label')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick?.(props.row.original as WonTitle)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text>{t('pages.application_won_title.components.won_title_table.won_title_table_view_details_menu_item')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original as WonTitle)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_won_title.components.won_title_table.won_title_table_delete_menu_item')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={isMultiTeam ? columns : columns.filter(col => (col as any).accessorKey !== 'team_name')}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
